import { useEffect, useState } from 'react';
import { Routes, Route, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HomePage } from './pages/Home/Home';
import { SearchPage } from './pages/Search/Search';
import { CreateAdPage } from './pages/CreateAd/CreateAd';
import { AdDetailsPage } from './pages/AdDetails/AdDetails';
import { EditAdPage } from './pages/EditAd/EditAd';
import { MyAdsPage } from './pages/MyAds/MyAds';
import { SettingsPage } from './pages/Settings/Settings';
import { FavoritesPage } from './pages/Favorites/Favorites';
import { useSync } from './hooks/useSync';
import { OfflineBanner } from './components/OfflineBanner/OfflineBanner';
import { SnapshotBanner } from './components/SnapshotBanner/SnapshotBanner';
import { UpdateToast } from './components/UpdateToast/UpdateToast';
import { Icon } from './components/Icon/Icon';

function NotFoundPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="empty-state">
      <span className="empty-state__icon">
        <Icon name="search" size={44} />
      </span>
      <h2 className="empty-state__text">{t('common.notFound')}</h2>
      <p className="empty-state__hint">{t('common.notFoundHint')}</p>
      <button className="btn btn--primary" onClick={() => navigate('/')}>
        {t('nav.home')}
      </button>
    </div>
  );
}

function SubscriptionMatchBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onMatch = () => {
      setVisible(true);
      setTimeout(() => setVisible(false), 6000);
    };
    window.addEventListener('barter:sub-match', onMatch);
    return () => window.removeEventListener('barter:sub-match', onMatch);
  }, []);

  if (!visible) return null;
  return (
    <div className="sub-match-banner" role="status" aria-live="polite">
      <span className="sub-match-banner__text">
        <Icon name="bell" size={18} /> {t('subscriptions.newMatch')}
      </span>
    </div>
  );
}

export function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { syncStatus, pendingCount } = useSync();
  const hideNav =
    location.pathname.startsWith('/ad/') ||
    location.pathname === '/create' ||
    location.pathname.startsWith('/edit/');
  const showBack = location.pathname !== '/';

  const syncDotClass =
    syncStatus === 'syncing'
      ? 'app-header__sync-dot app-header__sync-dot--syncing'
      : syncStatus === 'offline' || syncStatus === 'error'
        ? 'app-header__sync-dot app-header__sync-dot--offline'
        : 'app-header__sync-dot';

  return (
    <div className="app">
      <header className="app-header">
        {showBack && (
          <button className="app-header__back" onClick={() => navigate(-1)} aria-label="Back">
            <Icon name="chevron-left" size={22} />
          </button>
        )}
        <button
          className="app-header__logo"
          onClick={() => {
            if (location.pathname !== '/') navigate('/');
            else window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          aria-label={t('nav.home')}
          title={t('nav.home')}
          type="button"
        >
          <Icon name="swap" size={22} />
          {t('app.title')}
        </button>
        <span
          className="app-header__sync"
          title={`Sync: ${syncStatus}${pendingCount ? ` (${pendingCount} pending)` : ''}`}
        >
          <span className={syncDotClass} />
          {pendingCount > 0 && <span className="app-header__sync-count">{pendingCount}</span>}
        </span>
      </header>
      <OfflineBanner />
      <SnapshotBanner />
      <SubscriptionMatchBanner />
      <UpdateToast />
      <main className="app-main">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/create" element={<CreateAdPage />} />
          <Route path="/ad/:id" element={<AdDetailsPage />} />
          <Route path="/edit/:id" element={<EditAdPage />} />
          <Route path="/my" element={<MyAdsPage />} />
          <Route path="/favorites" element={<FavoritesPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      {!hideNav && (
        <nav className="app-nav">
          <NavLink
            to="/"
            end
            className={({ isActive }) => `app-nav__item${isActive ? ' app-nav__item--active' : ''}`}
          >
            <Icon name="home" size={23} />
            <span className="app-nav__label">{t('nav.home')}</span>
          </NavLink>
          <NavLink
            to="/search"
            className={({ isActive }) => `app-nav__item${isActive ? ' app-nav__item--active' : ''}`}
          >
            <Icon name="search" size={23} />
            <span className="app-nav__label">{t('nav.search')}</span>
          </NavLink>
          <NavLink to="/create" className="app-nav__item app-nav__item--create" aria-label={t('nav.create')}>
            <Icon name="plus" size={24} />
          </NavLink>
          <NavLink
            to="/favorites"
            className={({ isActive }) => `app-nav__item${isActive ? ' app-nav__item--active' : ''}`}
          >
            <Icon name="heart" size={23} />
            <span className="app-nav__label">{t('nav.favorites')}</span>
          </NavLink>
          <NavLink
            to="/my"
            className={({ isActive }) => `app-nav__item${isActive ? ' app-nav__item--active' : ''}`}
          >
            <Icon name="list" size={23} />
            <span className="app-nav__label">{t('nav.my')}</span>
          </NavLink>
          <NavLink
            to="/settings"
            className={({ isActive }) => `app-nav__item${isActive ? ' app-nav__item--active' : ''}`}
          >
            <Icon name="sliders" size={23} />
            <span className="app-nav__label">{t('nav.more')}</span>
          </NavLink>
        </nav>
      )}
    </div>
  );
}
